import React, { useState } from 'react';
import ThreeDTeethChart, { adultUpperTeeth, adultLowerTeeth, childUpperTeeth, childLowerTeeth } from './ThreeDTeethChart';
import ThreeDSkullChart, { skullPartsData } from './ThreeDSkullChart';
import { Phone, MapPin, Clock, Info, CheckCircle, AlertCircle, Moon, Sun, ChevronDown, ChevronUp } from 'lucide-react';

/* ─── Modal ─────────────────────────────────────── */
const Modal = ({ isOpen, onClose, type, message }) => {
    if (!isOpen) return null;
    return (
        <div className="trf-modal-overlay" role="dialog" aria-modal="true" aria-label={type === 'success' ? 'Success' : 'Error'}>
            <div className="trf-modal-box">
                <div className={`trf-modal-icon ${type}`}>
                    {type === 'success'
                        ? <CheckCircle size={52} />
                        : <AlertCircle size={52} />}
                </div>
                <p className="trf-modal-title">{type === 'success' ? 'Referral Sent!' : 'Something Went Wrong'}</p>
                <p className="trf-modal-message">{message}</p>
                <button className="trf-modal-close" onClick={onClose}>Close</button>
            </div>
        </div>
    );
};

/* ─── Main Form ──────────────────────────────────── */
export default function PatientReferralForm() {
    const [darkMode, setDarkMode]       = useState(false);
    const [teethType, setTeethType]     = useState('adult');
    const [modalState, setModalState]   = useState({ isOpen: false, type: 'success', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showTraumaForm, setShowTraumaForm] = useState(false);

    const [formData, setFormData] = useState({
        date:              new Date().toISOString().split('T')[0],
        referringDr:       '',
        phone:             '',
        patientName:       '',
        patientDOB:        '',
        patientPhone:      '',
        patientEmail:      '',
        procedures:        [],
        selectedTeeth:     [],
        facialTraumas:     [],
        dentalImplants: {
            area:          '',
            sinusLift:     false,
            boneGraft:     false,
            gingivalGraft: false,
            other:         '',
        },
        comments:              '',
        callBeforeTreatment:   false,
        radiographsSent:       false,
    });

    const procedureList = [
        'Extraction(s)', 'Wisdom Teeth Extraction', 'Bone Grafting', 'Expose & Bond',
        'Facial Trauma', 'Frenectomy', 'Gingivectomy', 'Infection Treatment',
        'Pre-Prosthetic Surgery', 'PRP/PRF', 'Orofacial/TMJ Pain', 'Oral Pathology',
        'Trigeminal Neuralgia', 'Tori Reduction', 'Biopsy/Pathology',
    ];

    const toggleProcedure = (p) =>
        setFormData(prev => ({
            ...prev,
            procedures: prev.procedures.includes(p)
                ? prev.procedures.filter(x => x !== p)
                : [...prev.procedures, p],
        }));

    const toggleTooth = (num) =>
        setFormData(prev => ({
            ...prev,
            selectedTeeth: prev.selectedTeeth.includes(num)
                ? prev.selectedTeeth.filter(t => t !== num)
                : [...prev.selectedTeeth, num],
        }));

    const toggleTrauma = (id) =>
        setFormData(prev => ({
            ...prev,
            facialTraumas: prev.facialTraumas.includes(id)
                ? prev.facialTraumas.filter(t => t !== id)
                : [...prev.facialTraumas, id],
        }));

    const closeModal = () => setModalState(s => ({ ...s, isOpen: false }));

    const formatPhone = (val) => {
        const d = val.replace(/\D/g, '');
        if (d.length < 4) return d;
        if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
    };

    const handlePhone = (e, field) =>
        setFormData(prev => ({ ...prev, [field]: formatPhone(e.target.value) }));

    const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
    const setImpl = (field, value) =>
        setFormData(prev => ({ ...prev, dentalImplants: { ...prev.dentalImplants, [field]: value } }));

    const handleSubmit = async () => {
        if (isSubmitting) return;
        if (!formData.referringDr || !formData.patientName) {
            setModalState({ isOpen: true, type: 'error', message: 'Please fill in required fields: Referring Doctor and Patient Name.' });
            const firstEmpty = !formData.referringDr ? 'refDr' : 'patName';
            document.getElementById(firstEmpty)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        setIsSubmitting(true);
        try {
            const res = await fetch('/api/referrals', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, submittedAt: new Date().toISOString() }),
            });
            if (res.ok) {
                setModalState({ isOpen: true, type: 'success', message: 'Your referral has been received. We will be in touch shortly.' });
                setFormData({
                    date: new Date().toISOString().split('T')[0], referringDr: '', phone: '',
                    patientName: '', patientDOB: '', patientPhone: '', patientEmail: '',
                    procedures: [], selectedTeeth: [], facialTraumas: [],
                    dentalImplants: { area: '', sinusLift: false, boneGraft: false, gingivalGraft: false, other: '' },
                    comments: '', callBeforeTreatment: false, radiographsSent: false,
                });
            } else {
                const err = await res.json();
                setModalState({ isOpen: true, type: 'error', message: `Error: ${err.message}` });
            }
        } catch {
            setModalState({ isOpen: true, type: 'error', message: 'Unable to submit. Please check your connection.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    /* tooth label helper */
    const allTeethData = [...adultUpperTeeth, ...adultLowerTeeth, ...childUpperTeeth, ...childLowerTeeth];
    const getToothLabel = (num) => {
        const tooth = allTeethData.find(t => t.num === num);
        if (!tooth) return `#${num}`;
        if (tooth.fullName) return `${num}: ${tooth.fullName}`;
        let name = tooth.type.charAt(0).toUpperCase() + tooth.type.slice(1);
        if (['1', '16', '17', '32'].includes(num)) name = 'Wisdom Tooth';
        return `${name} — ${tooth.label} (#${num})`;
    };

    return (
        <div className={`app${darkMode ? ' dark' : ''}`}>
            <Modal
                isOpen={modalState.isOpen}
                onClose={closeModal}
                type={modalState.type}
                message={modalState.message}
            />

            {/* ── Dark Mode Toggle ── */}
            <button
                className="trf-theme-toggle"
                onClick={() => setDarkMode(d => !d)}
                aria-label="Toggle dark mode"
            >
                {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* ── Header ── */}
            <header className="trf-header">
                <div style={{ position: 'relative', zIndex: 1 }}>
                    <div className="trf-header-logo-text">
                        <span style={{ color: '#ffffff' }}>Titanium</span> <span>Surgical Arts</span>
                    </div>
                    <p className="mt-3 mb-0" style={{ fontSize: '1rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>
                        Patient Referral Form
                    </p>
                </div>
                <div className="trf-header-divider" />
            </header>

            {/* ── Body ── */}
            <div className="trf-container">

                {/* ── Referring Doctor ── */}
                <section className="trf-card">
                    <h2 className="trf-section-title">
                        <Info size={14} /> Referring Doctor
                    </h2>
                    <div className="trf-grid-3">
                        <div>
                            <label htmlFor="refDate" className="trf-label">Date</label>
                            <input
                                id="refDate" type="date" className="trf-input"
                                value={formData.date}
                                onChange={e => set('date', e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="refDr" className="trf-label">
                                Doctor Name <span className="trf-required">*</span>
                            </label>
                            <input
                                id="refDr" type="text" className="trf-input"
                                placeholder="Dr. Tabarini"
                                value={formData.referringDr}
                                onChange={e => set('referringDr', e.target.value)}
                                aria-required="true"
                            />
                        </div>
                        <div>
                            <label htmlFor="refPhone" className="trf-label">Office Phone</label>
                            <input
                                id="refPhone" type="tel" className="trf-input"
                                placeholder="(555) 123-4567"
                                value={formData.phone}
                                onChange={e => handlePhone(e, 'phone')}
                            />
                        </div>
                    </div>
                </section>

                {/* ── Patient Details ── */}
                <section className="trf-card">
                    <h2 className="trf-section-title">
                        <Info size={14} /> Patient Details
                    </h2>
                    <div className="trf-grid-2 mb-3">
                        <div>
                            <label htmlFor="patName" className="trf-label">
                                Full Name <span className="trf-required">*</span>
                            </label>
                            <input
                                id="patName" type="text" className="trf-input"
                                placeholder="Jane Doe"
                                value={formData.patientName}
                                onChange={e => set('patientName', e.target.value)}
                                aria-required="true"
                            />
                        </div>
                        <div>
                            <label htmlFor="patDOB" className="trf-label">Date of Birth</label>
                            <input
                                id="patDOB" type="date" className="trf-input"
                                value={formData.patientDOB}
                                onChange={e => set('patientDOB', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="trf-grid-2">
                        <div>
                            <label htmlFor="patPhone" className="trf-label">Phone</label>
                            <input
                                id="patPhone" type="tel" className="trf-input"
                                placeholder="(555) 987-6543"
                                value={formData.patientPhone}
                                onChange={e => handlePhone(e, 'patientPhone')}
                            />
                        </div>
                        <div>
                            <label htmlFor="patEmail" className="trf-label">Email</label>
                            <input
                                id="patEmail" type="email" className="trf-input"
                                placeholder="jane@example.com"
                                value={formData.patientEmail}
                                onChange={e => set('patientEmail', e.target.value)}
                            />
                        </div>
                    </div>
                </section>

                {/* ── Evaluation For ── */}
                <section className="trf-card">
                    <h2 className="trf-section-title">
                        <CheckCircle size={14} /> Evaluation For
                    </h2>
                    <div className="trf-grid-3">
                        {procedureList.map(p => (
                            <label
                                key={p}
                                className={`trf-check-card${formData.procedures.includes(p) ? ' checked' : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={formData.procedures.includes(p)}
                                    onChange={() => toggleProcedure(p)}
                                />
                                {p}
                            </label>
                        ))}
                    </div>
                </section>

                {/* ── Tooth Selection ── */}
                <section className="trf-card">
                    <h2 className="trf-section-title">
                        <Info size={14} /> Tooth Selection
                    </h2>
                    <ThreeDTeethChart
                        selectedTeeth={formData.selectedTeeth}
                        onToothToggle={toggleTooth}
                        teethType={teethType}
                        onTypeChange={(t) => {
                            setTeethType(t);
                            setFormData(prev => ({ ...prev, selectedTeeth: [] }));
                        }}
                        darkMode={darkMode}
                    />
                    {formData.selectedTeeth.length > 0 && (
                        <div className="trf-tooth-summary">
                            <strong>Selected Teeth</strong>
                            {formData.selectedTeeth.map(n => getToothLabel(n)).join('  ·  ')}
                        </div>
                    )}
                </section>

                {/* ── Facial Trauma ── */}
                <section className="trf-card">
                    <div 
                        className="trf-section-title" 
                        style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer', marginBottom: showTraumaForm ? '16px' : '0' }}
                        onClick={() => setShowTraumaForm(!showTraumaForm)}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Info size={14} /> Facial Trauma Assessment <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)', fontWeight: 'normal' }}>(Optional)</span>
                        </div>
                        {showTraumaForm ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                    {showTraumaForm && (
                        <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
                            <ThreeDSkullChart
                                selectedTraumas={formData.facialTraumas}
                                onTraumaToggle={toggleTrauma}
                                darkMode={darkMode}
                            />
                            {formData.facialTraumas.length > 0 && (
                                <div className="trf-tooth-summary">
                                    <strong>Selected Regions</strong>
                                    {formData.facialTraumas.map(id => {
                                        const part = skullPartsData.find(p => p.id === id);
                                        return part ? part.label : id;
                                    }).join('  ·  ')}
                                </div>
                            )}
                        </div>
                    )}
                </section>

                {/* ── Dental Implants ── */}
                <section className="trf-card">
                    <h2 className="trf-section-title">
                        <Info size={14} /> Dental Implants
                    </h2>
                    <div className="trf-grid-2 mb-3">
                        <div>
                            <label className="trf-label">Area</label>
                            <input
                                type="text" className="trf-input"
                                placeholder="e.g. Upper Left"
                                value={formData.dentalImplants.area}
                                onChange={e => setImpl('area', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="trf-label">Other Notes</label>
                            <input
                                type="text" className="trf-input"
                                placeholder="Additional details..."
                                value={formData.dentalImplants.other}
                                onChange={e => setImpl('other', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="trf-grid-3">
                        {[
                            { key: 'sinusLift',     label: 'Sinus Lift' },
                            { key: 'boneGraft',     label: 'Bone Graft' },
                            { key: 'gingivalGraft', label: 'Gingival Graft' },
                        ].map(({ key, label }) => (
                            <label
                                key={key}
                                className={`trf-check-card${formData.dentalImplants[key] ? ' checked' : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={formData.dentalImplants[key]}
                                    onChange={e => setImpl(key, e.target.checked)}
                                />
                                {label}
                            </label>
                        ))}
                    </div>
                </section>

                {/* ── Comments ── */}
                <section className="trf-card">
                    <h2 className="trf-section-title">
                        <Info size={14} /> Additional Comments
                    </h2>
                    <textarea
                        className="trf-input mb-3"
                        rows={3}
                        placeholder="Additional comments or special instructions..."
                        value={formData.comments}
                        onChange={e => set('comments', e.target.value)}
                        style={{ resize: 'vertical' }}
                    />
                    <div className="trf-grid-2">
                        <label className={`trf-check-card${formData.callBeforeTreatment ? ' checked' : ''}`}>
                            <input
                                type="checkbox"
                                checked={formData.callBeforeTreatment}
                                onChange={e => set('callBeforeTreatment', e.target.checked)}
                            />
                            Call before treatment
                        </label>
                        <label className={`trf-check-card${formData.radiographsSent ? ' checked' : ''}`}>
                            <input
                                type="checkbox"
                                checked={formData.radiographsSent}
                                onChange={e => set('radiographsSent', e.target.checked)}
                            />
                            Radiographs sent
                        </label>
                    </div>
                </section>

                {/* ── Pre-Op Instructions ── */}
                <section className="trf-card">
                    <h2 className="trf-section-title">
                        <Clock size={14} /> Pre-Operative Instructions
                    </h2>
                    <div className="trf-preop-block">
                        <strong className="preop-heading">Local Anesthesia</strong>
                        Your mouth and teeth should be well cleansed immediately before your appointment.
                    </div>
                    <div className="trf-preop-block">
                        <strong className="preop-heading">General Anesthesia</strong>
                        You must not have anything to eat or drink after midnight the day before your procedure.
                        The evening before surgery, eat a light, easily digestible meal, consume no alcoholic beverages, and retire early.
                        <br /><br />
                        <strong>Disregarding the above eating instructions may necessitate cancellation and rescheduling of your surgery. This is for your safety.</strong>
                        <br /><br />
                        Wear comfortable, loose-fitting clothing. Avoid heavy makeup and lipstick, and do not wear jewelry.
                        You must have a designated driver (18+ with valid license) present in the waiting room during surgery.
                    </div>
                </section>

                {/* ── Footer info ── */}
                <div className="trf-footer-info">
                    <p style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}>
                        Fields marked <span style={{ color: '#e53e3e' }}>*</span> are required.
                    </p>
                </div>

                {/* ── Submit ── */}
                <button
                    className="trf-btn-submit"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    aria-label="Submit referral form"
                >
                    {isSubmitting ? (
                        <>
                            <span className="trf-spinner" />
                            Submitting…
                        </>
                    ) : 'Submit Referral'}
                </button>
            </div>
        </div>
    );
}
